import { useEffect, useState } from "react";
import { Alert, CircularProgress, Grid, Icon, InputLabel } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";
import { errorAlert, successAlert } from "utils/Functions";
import { get, post } from "utils/WebService";

// Conta de saída da Mala Direta (Parametrização do Sistema > Mala Direta): uma conta só para os envios em lote.
// Só Microsoft 365: envia pela API do Graph com o app da aba Microsoft 365, em nome da caixa (só o e-mail dela, sem
// senha). A tela não oferece mais SMTP (out/2026): a conta gravada em SMTP continua enviando assim até ser salva aqui, e
// a configuração de SMTP dela fica guardada no banco. Os limites dão o ritmo da fila. É uma caixa diferente da dos e-mails
// do sistema e dos robôs (aba Microsoft 365): cada caixa tem limite diário de envio; o backend recusa a mesma caixa.
const VAZIA = { nome: "", email: "", limitePorMinuto: 30, limitePorDia: 10000 };

export default function ContaSaidaMalaDireta() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const cores = coresDoTema(darkMode);

  const [conta, setConta] = useState(VAZIA);
  const [info, setInfo] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [emailTeste, setEmailTeste] = useState("");

  const carregar = () => {
    get("/mala-direta/conta")
      .then((res) => {
        setInfo(res);
        setConta({
          ...VAZIA,
          nome: res.nome || "",
          email: res.email || "",
          limitePorMinuto: res.limitePorMinuto ?? VAZIA.limitePorMinuto,
          limitePorDia: res.limitePorDia ?? VAZIA.limitePorDia,
        });
      })
      .catch((e) => errorAlert(e?.message || "Erro ao carregar a conta de saída."));
  };

  useEffect(carregar, []);

  const campo = (chave) => (e) => setConta({ ...conta, [chave]: e.target.value });

  const salvar = () => {
    setSalvando(true);
    post("/mala-direta/conta", { ...conta, tipo: "MICROSOFT365" })
      .then(() => {
        successAlert("Conta de saída salva.");
        carregar();
      })
      .catch((e) => errorAlert(e?.message || "Erro ao salvar a conta."))
      .finally(() => setSalvando(false));
  };

  const testar = () => {
    setTestando(true);
    post("/mala-direta/conta/testar", { email: emailTeste })
      .then(() => successAlert(`E-mail de teste enviado para ${emailTeste}. Confira a caixa de entrada (e o lixo eletrônico).`))
      .catch((e) => errorAlert(e?.message || "A conta não conseguiu enviar."))
      .finally(() => setTestando(false));
  };

  const conflito =
    !!conta.email && !!info?.caixaSistema && conta.email.trim().toLowerCase() === info.caixaSistema.trim().toLowerCase();

  const rotulo = (texto) => <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>{texto}</InputLabel>;
  const ajuda = (texto) => (
    <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5 }}>
      {texto}
    </MDTypography>
  );

  if (!info) {
    return (
      <MDBox display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} color="info" />
      </MDBox>
    );
  }

  return (
    <MDBox sx={{ background: cores.painel, border: `1px solid ${cores.divisoria}`, borderRadius: 2, p: 2 }}>
      {info.ambienteTeste && (
        <Alert severity={info.emailDeTeste ? "info" : "warning"} sx={{ mb: 2, fontSize: "0.85rem" }}>
          {info.emailDeTeste
            ? <>Este ambiente não é o de produção: os e-mails dos lotes vão para <b>{info.emailDeTeste}</b>, não para os devedores (o assunto diz para quem iria).</>
            : <>Este ambiente não é o de produção e não tem e-mail para receber os envios: configure <b>app.mail.dev-override</b> ou o seu e-mail no cadastro de usuário. Sem isso, a fila registra erro em vez de enviar.</>}
        </Alert>
      )}
      <Alert severity="info" sx={{ mb: 2, fontSize: "0.85rem" }}>
        Esta caixa é só da Mala Direta. Os e-mails do sistema (recuperação de senha, código de acesso, avisos) e dos robôs saem por
        outra caixa{info.caixaSistema ? <>: <b>{info.caixaSistema}</b></> : ""}, configurada na aba <b>Microsoft 365</b>. Cada caixa
        tem limite diário de envio: por isso as duas são diferentes.
      </Alert>
      {!info.configurada && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: "0.85rem" }}>
          A conta de saída ainda não foi configurada: sem ela, a Mala Direta não envia.
        </Alert>
      )}
      {!info.microsoft365Configurado && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: "0.85rem" }}>
          O app do Microsoft 365 ainda não foi configurado: preencha a aba <b>Microsoft 365</b> desta tela antes de salvar a conta.
        </Alert>
      )}
      {info.configurada && info.tipo !== "MICROSOFT365" && (
        <Alert severity="info" sx={{ mb: 2, fontSize: "0.85rem" }}>
          A conta gravada ainda envia por SMTP (usuário e senha). Ao salvar, ela passa a enviar pelo Microsoft 365, sem senha.
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          {rotulo("E-mail da caixa que envia")}
          <MDInput size="small" fullWidth value={conta.email} onChange={campo("email")} placeholder="cobranca@raadvogados.adv.br" error={conflito} />
          {conflito ? (
            <MDTypography variant="caption" color="error" display="block" sx={{ px: 1, mt: 0.5 }}>
              Esta é a caixa dos e-mails do sistema. Use outra caixa para a Mala Direta.
            </MDTypography>
          ) : (
            ajuda("Uma caixa do Microsoft 365 do escritório que o app pode usar. Envia pelo app da aba Microsoft 365, sem senha; a cópia fica nos Itens Enviados da caixa.")
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {rotulo("Nome da conta")}
          <MDInput size="small" fullWidth value={conta.nome} onChange={campo("nome")} placeholder="Ex.: Rezende Advogados - Cobrança" />
          {ajuda("Só para identificar a conta aqui: quem recebe vê o nome cadastrado na própria caixa.")}
        </Grid>
        <Grid item xs={6} md={2}>
          {rotulo("Limite por minuto")}
          <MDInput size="small" fullWidth type="number" value={conta.limitePorMinuto ?? ""} onChange={campo("limitePorMinuto")} />
          {ajuda("Mensagens por minuto na fila (até 30).")}
        </Grid>
        <Grid item xs={6} md={2}>
          {rotulo("Limite por dia")}
          <MDInput size="small" fullWidth type="number" value={conta.limitePorDia ?? ""} onChange={campo("limitePorDia")} />
          {ajuda("Destinatários em 24 h (até 10.000 por caixa).")}
        </Grid>
        <Grid item xs={12} md={4}>
          {rotulo("E-mail para o teste da conta")}
          <MDInput size="small" fullWidth value={emailTeste} onChange={(e) => setEmailTeste(e.target.value)} placeholder="seu.email@raadvogados.adv.br" />
        </Grid>
        <Grid item xs={12} md={8}>
          <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>&nbsp;</InputLabel>
          <MDBox display="flex" gap={1} flexWrap="wrap">
            <MDButton variant="outlined" color="info" size="medium" sx={{ height: 38 }} onClick={testar} disabled={testando || !info.configurada || !emailTeste}>
              {testando ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} /> : <Icon sx={{ mr: 0.5 }}>outgoing_mail</Icon>}
              {testando ? "Enviando..." : "Enviar e-mail de teste"}
            </MDButton>
            <MDButton variant="contained" color="info" size="medium" sx={{ height: 38 }} onClick={salvar} disabled={salvando || !info.microsoft365Configurado || conflito}>
              {salvando ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} /> : <Icon sx={{ mr: 0.5 }}>save</Icon>}
              {salvando ? "Salvando..." : "Salvar conta de saída"}
            </MDButton>
          </MDBox>
          {ajuda("O teste usa a conta gravada: salve antes de testar uma mudança.")}
        </Grid>
      </Grid>
    </MDBox>
  );
}
